<template>
  <section class="wbs-step" aria-labelledby="wbs-heading" data-testid="website-step">
    <div class="wbs-heading">
      <h2 id="wbs-heading" class="wbs-title" data-testid="wbs-title">{{ title }}</h2>
      <p v-if="subtitle" class="wbs-subtitle" data-testid="wbs-subtitle">{{ subtitle }}</p>
    </div>

    <div class="wbs-content">
      <CompanyWebsitesField
        :websites="websiteList"
        :show-heading="false"
        @add="addWebsite"
        @remove="removeWebsite"
      />
      <p class="wbs-note" data-testid="wbs-note">{{ noteText }}</p>
    </div>

    <div class="wbs-spacer" aria-hidden="true"></div>

    <div class="wbs-actions">
      <button
        type="button"
        class="wbs-btn wbs-btn--primary"
        :disabled="!canContinue"
        data-testid="wbs-continue"
        @click="handleContinue"
      >
        {{ buttonLabel }}
      </button>
      <button
        type="button"
        class="wbs-btn wbs-btn--secondary"
        data-testid="wbs-skip"
        @click="emit('skip')"
      >
        Skip for now
      </button>
      <button
        type="button"
        class="wbs-btn wbs-btn--secondary"
        data-testid="wbs-do-not-ask"
        @click="emit('do-not-ask')"
      >
        Do not ask again
      </button>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from "vue";
import CompanyWebsitesField from "@components/CompanyWebsitesField/CompanyWebsitesField.vue";

/**
 * ER-12246 — WebsiteStep (MDP-621).
 *
 * "Add your company website" (page key `website`): enters websites with the shared
 * Company websites component of IQ Settings (ER-12778). Continue is disabled until at
 * least one website has been added; Skip for now and Do not ask again are always
 * available. Presentational — Continue emits the list and the flow controller saves
 * it (ER-12244).
 */
const props = defineProps({
  title: { type: String, default: "Add your company website" },
  subtitle: {
    type: String,
    default:
      "IQ Agent reads your public websites to learn about your organization and give answers that fit your company.",
  },
  noteText: {
    type: String,
    default:
      "IQ scans the websites and stores what it finds as account context. You can change them later in IQ settings.",
  },
  /** Websites already on the account, if any. */
  websites: { type: Array, default: () => [] },
  buttonLabel: { type: String, default: "Continue" },
});

const emit = defineEmits(["continue", "skip", "do-not-ask"]);

const websiteList = ref([...props.websites]);

const canContinue = computed(() => websiteList.value.length > 0);

function addWebsite(url) {
  if (!websiteList.value.includes(url)) websiteList.value.push(url);
}

function removeWebsite(url) {
  websiteList.value = websiteList.value.filter((website) => website !== url);
}

function handleContinue() {
  if (!canContinue.value) return;
  emit("continue", { websites: [...websiteList.value] });
}
</script>

<style scoped>
.wbs-step {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  height: 100%;
  padding: 0;
  background: #ffffff;
  color: #101828;
  font-family: Inter, sans-serif;
}

.wbs-heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wbs-title {
  margin: 0;
  color: #101828;
  font-size: 28px;
  font-weight: 500;
  line-height: 34px;
}

.wbs-subtitle {
  margin: 0;
  color: #475467;
  font-size: 16px;
  line-height: 24px;
}

.wbs-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.wbs-note {
  margin: 0;
  color: #667085;
  font-size: 14px;
  line-height: 20px;
}

.wbs-spacer {
  flex: 1 1 auto;
}

.wbs-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.wbs-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  min-width: 120px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.wbs-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.wbs-btn--primary:disabled {
  background: #eaecf0;
  border-color: #eaecf0;
  color: #475467;
  cursor: not-allowed;
  box-shadow: none;
}

.wbs-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

.wbs-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}
</style>
